import type { ReactNode } from "react"

import { useTranslations } from "next-intl"

import { Link } from "@/i18n/navigation"
import { tv, type VariantProps } from "@/lib/utils/tv"

import { HeadingMark, MoneyMark } from "./heading-mark"

// See-all is a text link on a 1px ink rule, sitting on the headline's last
// baseline. A chevron makes the header a toolbar; the label makes it a masthead.
const SEE_ALL_VARIANT = {
  underline: "text-body @3xl:text-body-lg underline underline-offset-4",
  rule: "text-copy @3xl:text-copy-lg border-b border-current",
}

export const SeeAllButton = ({
  href,
  tone = "default",
  label,
  variant = "rule",
  className = "",
}: {
  href?: string
  tone?: "default" | "inverse"
  label?: string
  variant?: keyof typeof SEE_ALL_VARIANT
  className?: string
}) => {
  const t = useTranslations("marketplace.layout")
  const ink = tone === "inverse" ? "text-fg-inverted focus-visible:ring-fg-inverted" : "text-brand hover:text-brand-hover focus-visible:ring-brand"
  return href ? (
    <Link
      href={href}
      className={`${ink} ${SEE_ALL_VARIANT[variant]} hit-target font-text inline-flex shrink-0 items-center font-normal focus-visible:ring-2 focus-visible:ring-offset-4 focus-visible:outline-none ${className}`}
    >
      {label ?? t("viewAll")}
    </Link>
  ) : null
}

// One header, four voices — so a page's chapters don't all speak in the same
// register. `size` sets the scale, `tone` flips it for dark stages, `numeral`
// (chapter only) stamps the act number in oversized display type.
const header = tv({
  slots: {
    // flex-end puts the see-all on the headline's last baseline; flex-wrap with
    // a two-value gap is the whole mobile stack, no media query.
    root: "flex flex-wrap items-end justify-between gap-x-800 gap-y-350",
    eyebrow: "text-fg-muted font-display tracking-widest uppercase",
    title: "font-text text-section-head max-w-title text-balance",
    numeral: "font-display pointer-events-none leading-none font-extrabold tracking-tight tabular-nums select-none",
  },
  variants: {
    // One masthead size across every surface — Inter Semi Bold, 20→28px (title
    // slot). `size` now only tunes the eyebrow rhythm and the chapter numeral.
    size: {
      default: { eyebrow: "text-overline mb-100" },
      display: { eyebrow: "text-label mb-150" },
      // Act opener — oversized chapter numeral beside the masthead.
      chapter: { eyebrow: "text-label mb-150", numeral: "text-display-lg @3xl:text-display-2xl -mb-100 @3xl:-mb-200" },
      utility: { eyebrow: "text-overline mb-100" },
    },
    tone: {
      default: { title: "text-fg-strong", numeral: "text-palette-accent opacity-15" },
      inverse: { title: "text-fg-inverted", eyebrow: "text-on-scrim-muted", numeral: "text-fg-inverted opacity-20" },
    },
  },
  defaultVariants: { size: "default", tone: "default" },
})

interface SectionHeaderProps extends VariantProps<typeof header> {
  title: ReactNode
  accent?: ReactNode
  eyebrow?: ReactNode
  action?: ReactNode
  // Chapter number rendered at display scale (size="chapter" only).
  numeral?: string
  money?: string
  className?: string
}

const withMoney = (title: ReactNode, money?: string): ReactNode => {
  if (!money || typeof title !== "string") return title
  const at = title.indexOf(money)
  if (at < 0) return title
  return [title.slice(0, at), <MoneyMark key="money">{money}</MoneyMark>, title.slice(at + money.length)]
}

export const SectionHeader = ({ title, accent, eyebrow, action, numeral, money, size, tone, className }: SectionHeaderProps) => {
  const styles = header({ size, tone })
  return (
    <div className={styles.root({ className })}>
      {size === "chapter" && numeral && (
        <span className={styles.numeral()} aria-hidden>
          {numeral}
        </span>
      )}
      <div className="min-w-0">
        {eyebrow && <div className={styles.eyebrow()}>{eyebrow}</div>}
        <h2 className={styles.title()}>
          {withMoney(title, money)}
          {accent && <HeadingMark>{accent}</HeadingMark>}
        </h2>
      </div>
      {action}
    </div>
  )
}
